import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CouponService } from '../coupon.service';
import { Coupon } from '../coupon.model';
import { NotificationService } from 'src/app/shared/notification.service';

@Component({
  selector: 'app-coupon-dialog',
  templateUrl: './coupon-dialog.component.html',
  styleUrls: ['./coupon-dialog.component.css']
})
export class CouponDialogComponent {

  readonly maxStoredValueAmount = 500;

  form: FormGroup;
  isEdit: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Coupon | null,
    private dialogRef: MatDialogRef<CouponDialogComponent>,
    private fb: FormBuilder,
    private couponService: CouponService,
    private notifierService: NotificationService
  ) {
    this.isEdit = !!data;

    const today = new Date();
    const in30Days = new Date();
    in30Days.setDate(today.getDate() + 30);

    this.form = this.fb.group({
      code: [
        { value: data?.code || this.generateRandomCode(), disabled: this.isEdit },
        [Validators.required, Validators.pattern('^(?=.*[0-9])[A-Z0-9]{4}$')]
      ],
      description: [data?.description || ''],
      discountType: ['fixed', Validators.required],
      discountValue: [data?.discountValue || null, [Validators.required, Validators.min(1), Validators.max(this.maxStoredValueAmount)]],
      minOrderAmount: [data?.minOrderAmount ?? 0],
      validFrom: [data?.validFrom?.toDate ? data.validFrom.toDate() : today, Validators.required],
      validUntil: [data?.validUntil?.toDate ? data.validUntil.toDate() : in30Days, Validators.required],
      isActive: [data?.isActive !== undefined ? data.isActive : true]
    });
  }

  private generateRandomCode(): string {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const allChars = letters + digits;
    const codeChars = [digits.charAt(Math.floor(Math.random() * digits.length))];

    for (let i = 0; i < 3; i++) {
      codeChars.push(allChars.charAt(Math.floor(Math.random() * allChars.length)));
    }

    return codeChars
      .sort(() => Math.random() - 0.5)
      .join('');
  }

  generateCode() {
    this.form.get('code').setValue(this.generateRandomCode());
  }

  get previewText(): string {
    const value = this.form.get('discountValue').value;
    const min = this.form.get('minOrderAmount').value;

    if (!value) return '';

    let text = `Stores NOK ${value} on the gavekort`;

    if (min > 0) {
      text += ` orders above NOK ${min}`;
    }
    return text;
  }

  get remainingBalancePreview(): number | null {
    const value = Number(this.form.get('discountValue').value);

    if (!value) {
      return null;
    }

    if (!this.isEdit || !this.data || this.data.discountType !== 'fixed') {
      return value;
    }

    const previousRemaining = this.data.remainingBalance ?? this.data.discountValue;
    const consumedAmount = Math.max(this.data.discountValue - previousRemaining, 0);
    return Math.max(value - consumedAmount, 0);
  }

  async onSave() {
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();

    if (Number(raw.discountValue) > this.maxStoredValueAmount) {
      this.notifierService.showNotification('error', `Gavekort cannot be more than NOK ${this.maxStoredValueAmount}`);
      return;
    }

    const payload: Partial<Coupon> = {
      ...raw,
      discountType: 'fixed',
      code: raw.code.toUpperCase(),
      validFrom: new Date(raw.validFrom),
      validUntil: new Date(raw.validUntil),
      remainingBalance: this.remainingBalancePreview
    };

    try {
      if (this.isEdit) {
        const { code, ...updateData } = payload;
        await this.couponService.updateCoupon(this.data.code, updateData);
        this.notifierService.showNotification('success', 'Coupon updated');
      } else {
        await this.couponService.createCoupon(payload);
        this.notifierService.showNotification('success', 'Coupon created');
      }
      this.dialogRef.close(true);
    } catch (err) {
      this.notifierService.showNotification('error', err.message || 'Failed to save coupon');
    }
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
